import { tenantSlug } from './tg';

// Заголовок X-Telegram-Init-Data добавляется глобальным патчем fetch в <head>
// (как в референсе gb) — здесь его руками ставить не нужно.

export interface Plan {
  id: number;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  is_lifetime: boolean;
  kind: 'simple' | 'category_bundle' | 'vip';
  category: string | null;
  /** Иконка категории для панели категорий; null — покажем монограмму. */
  category_image: string | null;
  /** «Что внутри» — готовый список с сервера. */
  highlights: string[];
  /** Подписчиков в канале; null — ещё не считали или канала нет. */
  subscribers: number | null;
}

export interface Quote {
  subtotal: number;
  total: number;
  discount_percent: number;
}

export interface PayLink {
  pay_url: string | null;
  external_id: string | null;
  extra: Record<string, unknown> | null;
}

export interface Method {
  code: 'stars' | 'cactus' | 'rollypay' | 'cryptopay';
  title: string;
  note: string | null;
  assets: string[] | null;
}

export interface Config {
  title: string;
  all_label: string;
  show_all_category: boolean;
  show_old_price: boolean;
  old_price_multiplier: number;
  welcome_enabled: boolean;
  /** Открывать ли витрину с ПК/веба. false — только с телефона. */
  desktop_enabled: boolean;
  /** Скин витрины: '1' — стандартный, '2' — другой (для магазина-моста). */
  theme: string;
  /** Язык покупателя ('ru'/'en') — под него грузим словарь UI-строк. */
  locale: string;
}

export interface Banner {
  kind: 'expiring' | 'promo' | 'bundle';
  title: string;
  subtitle: string | null;
  plan_id: number | null;
  promo: string | null;
  /** ISO-момент дедлайна; таймер крутит фронт. */
  deadline: string | null;
}

export interface Subscription {
  plan_name: string;
  expires_at: string | null;
  is_lifetime: boolean;
}

/** Ошибка запроса к API с кодом ответа — чтобы витрина могла объяснить причину. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(`${status}: ${detail}`);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const slug = tenantSlug();
  if (!slug) throw new ApiError(0, 'магазин не определён');
  const resp = await fetch(`/api/${slug}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    throw new ApiError(resp.status, detail || resp.statusText);
  }
  return (await resp.json()) as T;
}

export const api = {
  catalog: () => request<Plan[]>('/catalog'),

  config: () => request<Config>('/config'),

  quote: (planIds: number[], promo?: string) =>
    request<Quote>('/quote', {
      method: 'POST',
      body: JSON.stringify({ plan_ids: planIds, promo: promo || null }),
    }),

  methods: () => request<Method[]>('/methods'),

  banners: () => request<Banner[]>('/banners'),

  subscriptions: () => request<Subscription[]>('/subscriptions'),

  payStars: (planIds: number[], promo?: string) =>
    request<PayLink>('/pay/stars', {
      method: 'POST',
      body: JSON.stringify({ plan_ids: planIds, promo: promo || null }),
    }),

  pay: (provider: string, planIds: number[], asset?: string, promo?: string) =>
    request<PayLink>(`/pay/${provider}`, {
      method: 'POST',
      body: JSON.stringify({ plan_ids: planIds, promo: promo || null, asset: asset || null }),
    }),
};
